/**
 * Atlas AI Chat Panel store.
 *
 * Drives the global slide-in AI panel that lives on the right edge of the page
 * while the user is inside the Atlas Intel route subtree. Owns:
 *   - open/close (sticky-open per session via sessionStorage)
 *   - resize width (persisted per-user via localStorage, 320–640 px)
 *   - per-route conversation threads (path → chatId, persisted)
 *   - context chips (route, filters, dateRange, hoveredMetric, selectedRec)
 *   - send() → dispatches a window event consumed by the existing React
 *     query-ai chat (../query-ai/components/ai-chat.tsx listens for
 *     `query-ai:open-chat-with-question`). Transport is unchanged.
 *
 * Per-route threads are implemented by varying the React chat's `connectionId`
 * per path — the existing chat-session-list backend (/platform-data/query-ai/chat/sessions)
 * scopes sessions by connectionId, so each path gets its own session bucket.
 */

import { defineStore } from 'pinia'
import { ref, computed, watch } from 'vue'

export interface ChatMessage {
  id?: string
  role: 'user' | 'assistant'
  content: string
  ts?: number
}

export interface ContextChip {
  id: string
  label: string
  kind: 'route' | 'cluster' | 'project' | 'dateRange' | 'metric' | 'rec' | 'filter' | string
  removable?: boolean
  data?: unknown
}

export interface OpenWithContextRec {
  kind: 'rec'
  rec: { id?: string; title?: string; [k: string]: unknown }
  question?: string
}
export interface OpenWithContextMetric {
  kind: 'metric'
  metric: { id: string; label?: string; [k: string]: unknown }
  question?: string
}
export interface OpenWithContextGeneric {
  kind: string
  question?: string
  [k: string]: unknown
}
export type OpenWithContext = OpenWithContextRec | OpenWithContextMetric | OpenWithContextGeneric

const SESSION_OPEN_KEY = 'aiChatPanel.openInSession'
const WIDTH_KEY = 'aiChatPanel.widthPx'
const THREADS_KEY = 'aiChatPanel.threadsByPath'
const MUTED_CHIPS_KEY = 'aiChatPanel.mutedChipIds'

export const PANEL_WIDTH_DEFAULT = 420
export const PANEL_WIDTH_MIN = 320
export const PANEL_WIDTH_MAX = 640

/** Stable connectionId for a given route path. Scopes React chat sessions
 *  per-route so switching routes loads/creates distinct threads. */
export function connectionIdForPath(path: string): string {
  return `atlas-intel:${path}`
}

function readJSON<T>(key: string, fallback: T): T {
  try {
    const raw = (typeof localStorage !== 'undefined' && localStorage.getItem(key)) || null
    if (!raw) return fallback
    return JSON.parse(raw) as T
  } catch {
    return fallback
  }
}

function readNumber(key: string, fallback: number): number {
  if (typeof localStorage === 'undefined') return fallback
  const raw = localStorage.getItem(key)
  if (raw == null) return fallback
  const n = parseInt(raw, 10)
  return Number.isFinite(n) ? n : fallback
}

function clampWidth(n: number): number {
  if (!Number.isFinite(n)) return PANEL_WIDTH_DEFAULT
  return Math.max(PANEL_WIDTH_MIN, Math.min(PANEL_WIDTH_MAX, Math.round(n)))
}

export const useAiChatPanelStore = defineStore('aiChatPanel', () => {
  // ── State ────────────────────────────────────────────────────────────────
  const open = ref<boolean>(
    typeof sessionStorage !== 'undefined' && sessionStorage.getItem(SESSION_OPEN_KEY) === '1'
  )
  const widthPx = ref<number>(clampWidth(readNumber(WIDTH_KEY, PANEL_WIDTH_DEFAULT)))
  const threadsByPath = ref<Record<string, string>>(
    readJSON<Record<string, string>>(THREADS_KEY, {})
  )
  const messagesByThread = ref<Record<string, ChatMessage[]>>({})
  const contextChips = ref<ContextChip[]>([])
  const mutedChipIds = ref<Set<string>>(new Set<string>(readJSON<string[]>(MUTED_CHIPS_KEY, [])))

  // Hover/selection signals (set by KpiCard hover and Recommendations clicks)
  const hoveredMetric = ref<string | null>(null)
  const selectedRec = ref<{ id?: string; title?: string; [k: string]: unknown } | null>(null)

  // Drives the React chat: latest seed question + autoRun flag.
  const seedQuestion = ref<string>('')
  const seedAutoRun = ref<boolean>(false)
  // Bumps every dispatch so AiChatPanel.vue can watch and re-fire.
  const seedNonce = ref<number>(0)

  // Currently active route path (driven by AiChatPanel.vue via setRoutePath).
  const routePath = ref<string>('')

  // ── Persistence side-effects ─────────────────────────────────────────────
  watch(open, (v) => {
    try {
      if (typeof sessionStorage === 'undefined') return
      if (v) sessionStorage.setItem(SESSION_OPEN_KEY, '1')
      else sessionStorage.removeItem(SESSION_OPEN_KEY)
    } catch {
      /* ignore quota/security errors */
    }
  })

  watch(widthPx, (v) => {
    try {
      if (typeof localStorage === 'undefined') return
      localStorage.setItem(WIDTH_KEY, String(v))
    } catch {
      /* ignore */
    }
  })

  watch(
    threadsByPath,
    (v) => {
      try {
        if (typeof localStorage === 'undefined') return
        localStorage.setItem(THREADS_KEY, JSON.stringify(v))
      } catch {
        /* ignore */
      }
    },
    { deep: true }
  )

  // ── Getters ──────────────────────────────────────────────────────────────
  const visibleChips = computed(() =>
    contextChips.value.filter((c) => !mutedChipIds.value.has(c.id))
  )

  const activeChatId = computed<string>(() => {
    const path = routePath.value
    if (!path) return ''
    return threadsByPath.value[path] ?? connectionIdForPath(path)
  })

  // ── Actions ──────────────────────────────────────────────────────────────
  function setOpen(v: boolean) {
    open.value = v
  }
  function toggle() {
    open.value = !open.value
  }
  function close() {
    open.value = false
  }

  function setWidth(px: number) {
    widthPx.value = clampWidth(px)
  }

  function setRoutePath(path: string) {
    routePath.value = path
    if (path && !threadsByPath.value[path]) {
      threadsByPath.value = {
        ...threadsByPath.value,
        [path]: connectionIdForPath(path)
      }
    }
  }

  /** Ensures a thread exists for path; returns the chatId/connectionId. */
  function loadThreadForPath(path: string): string {
    if (!threadsByPath.value[path]) {
      threadsByPath.value = {
        ...threadsByPath.value,
        [path]: connectionIdForPath(path)
      }
    }
    return threadsByPath.value[path]
  }

  /** Force the panel to a specific thread id (used by atlas-ai-history). */
  function loadThread(chatId: string, path?: string) {
    const p = path ?? routePath.value
    if (p) {
      threadsByPath.value = { ...threadsByPath.value, [p]: chatId }
    }
    open.value = true
  }

  function setChips(chips: ContextChip[]) {
    contextChips.value = chips
  }

  function clearChip(id: string) {
    if (mutedChipIds.value.has(id)) return
    const next = new Set(mutedChipIds.value)
    next.add(id)
    mutedChipIds.value = next
    try {
      if (typeof localStorage !== 'undefined')
        localStorage.setItem(MUTED_CHIPS_KEY, JSON.stringify([...next]))
    } catch {
      /* ignore */
    }
  }

  function unmuteAllChips() {
    mutedChipIds.value = new Set<string>()
    try {
      if (typeof localStorage !== 'undefined') localStorage.removeItem(MUTED_CHIPS_KEY)
    } catch {
      /* ignore */
    }
  }

  function setHoveredMetric(id: string | null) {
    hoveredMetric.value = id
  }
  function setSelectedRec(rec: { id?: string; title?: string; [k: string]: unknown } | null) {
    selectedRec.value = rec
  }

  /**
   * Send a question into the active thread. The transport is the existing
   * React query-ai chat which listens for `query-ai:open-chat-with-question`.
   */
  function send(text: string, opts?: { autoRun?: boolean }) {
    const q = text.trim()
    if (!q) return
    seedQuestion.value = q
    seedAutoRun.value = opts?.autoRun ?? true
    seedNonce.value += 1
    open.value = true
    if (typeof window !== 'undefined') {
      window.dispatchEvent(
        new CustomEvent('query-ai:open-chat-with-question', {
          detail: { question: q, autoRun: opts?.autoRun ?? true }
        })
      )
    }
  }

  /**
   * Open the panel with arbitrary context. Used by W3 (Recommendations) and
   * other surfaces that want to deep-link into the chat with a hint.
   *
   * Examples:
   *   openWithContext({ kind: 'rec', rec, question: 'Why was this rejected?' })
   *   openWithContext({ kind: 'metric', metric: { id: 'reads', label: 'Reads/sec' } })
   */
  function openWithContext(ctx: OpenWithContext) {
    open.value = true
    if (ctx.kind === 'rec') {
      const recCtx = ctx as OpenWithContextRec
      selectedRec.value = recCtx.rec ?? null
    } else if (ctx.kind === 'metric') {
      const mCtx = ctx as OpenWithContextMetric
      hoveredMetric.value = mCtx.metric?.id ?? null
    }
    if (ctx.question && typeof ctx.question === 'string') {
      send(ctx.question, { autoRun: true })
    }
  }

  return {
    // state
    open,
    widthPx,
    threadsByPath,
    messagesByThread,
    contextChips,
    mutedChipIds,
    hoveredMetric,
    selectedRec,
    seedQuestion,
    seedAutoRun,
    seedNonce,
    routePath,
    // getters
    visibleChips,
    activeChatId,
    // actions
    setOpen,
    toggle,
    close,
    setWidth,
    setRoutePath,
    loadThreadForPath,
    loadThread,
    setChips,
    clearChip,
    unmuteAllChips,
    setHoveredMetric,
    setSelectedRec,
    send,
    openWithContext
  }
})
